
    // IMPORTS
    import { defineComponent, PropType, computed, watchEffect, reactive } from 'vue'
    import { useUserStateStore } from '@/store/userState'
    import { useEquipmentStore } from '@/store/equipment';

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import CloseButtonHeader from '@/components/common/close-button-header.vue'

    import { MaterialListItem } from '@/types/material'
    import { IPartOf, ISearchInput, ISearchMatch, ISearchResult, ISearchResultGroup } from '@/types/search'
    import { buildClassificationLocalization, buildLocalization, StringUtil, waitUntil } from '@/util';

    export interface ISearchPanelState {
        isUpdating: boolean,
        currentSearchText?: string,
        result: ISearchResult,
        selectedMatch: ISearchMatch | null
    }

    export interface ISearchTarget {
        searchText?: string,
        searchNumber?: number,
        searchRegex?: RegExp | null
    }

    export default defineComponent({
        name: 'searchResultPanel',

        components: {
            LoaderGuard,
            Card,
            CardHeader,
            CardTitle,
            CardBody,
            CloseButtonHeader
        },

        props: {
            searchInput: { type: Object as PropType<ISearchInput>, required: true },
            data: { type: Array as PropType<MaterialListItem[]>, required: true },

            // Only allow materials in the search results, which exist on the active equipment
            equipmentMode: { type: Boolean as PropType<boolean>, required: false, default: () => true }
        },

        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            activeMatch: (selectedMatch: ISearchMatch) => true,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onClose: () => true
        },


        setup(props, { emit }) {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const userStateStore = useUserStateStore()
            const equipmentStore = useEquipmentStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const state = reactive({
                isUpdating: false,
                result: {
                    groups: []
                },
                selectedMatch: null
            } as ISearchPanelState)

            const publicData = { state }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const searchResultCount = computed(() => state.result.groups
                .reduce((sum, group) => sum + (group.directMatch != null ? 1 : 0) + group.partMatches.length, 0)
            )

            const searchText = computed(() => props.searchInput?.searchText)

            const anyActiveSearch = computed(() => StringUtil.hasContent(searchText.value))
            const searchTarget = computed<ISearchTarget | null>(() => anyActiveSearch.value
                ? {
                    searchText: searchText.value?.trim().toLowerCase(),
                    searchNumber: parseInt(searchText.value),
                    searchRegex: getSearchRegex(searchText.value?.trim())
                }
                : null)

            const computedData = { searchResultCount }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function gotoSearchResult(match: ISearchMatch) {
                state.selectedMatch = match
                emit('activeMatch', match)
            }

            function onClose() {
                userStateStore.setSparePartSearchText(null)
                emit('onClose')                    
            }

            const publicMethods = { localize, gotoSearchResult, onClose }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            const { classificationHasLoaded } = buildClassificationLocalization()

            function determineSearchResults(searchInput: ISearchInput) {
                if (searchInput.searchText === state.currentSearchText) return
                state.isUpdating = true

                waitUntil(() => classificationHasLoaded.value)
                    .then(() => {
                        const searchResult = getSearchResult(searchTarget.value)
                        state.result = searchResult
                        state.currentSearchText = searchInput.searchText

                        // Auto jump to first search result?
                        if (searchInput.jumpToFirstResult && searchResult.groups.length) {
                            const firstGroup = searchResult.groups[0]

                            if (firstGroup.directMatch) {
                                gotoSearchResult(firstGroup.directMatch)
                            }
                            else if (firstGroup.partMatches.length) {
                                gotoSearchResult(firstGroup.partMatches[0].match)
                            }
                        }
                    })
                    .finally(() => state.isUpdating = false)
            }

            function getSearchResult(target: ISearchTarget | null): ISearchResult {
                if (target == null) {
                    return {
                        groups: []
                    }
                }

                // First retrieve all matches
                const allMatches = getSearchMatches(target, props.data)

                // Now group
                const groups: ISearchResultGroup[] = []

                let relevantMatches: ISearchMatch[] | null

                if (props.equipmentMode && equipmentStore.bomItemQuantityDict != null) {
                    // Remove search results for materials not present in the active equipment!
                    const dict = equipmentStore.bomItemQuantityDict
                    relevantMatches = allMatches
                        .filter(m => m.node.materialNo && dict[m.node.materialNo] != null)
                }
                else {
                    relevantMatches = allMatches
                        .filter(m => m.node.materialNo)
                }

                const matchLookup = relevantMatches
                    .toLookup(m => m.node.materialNo?.toString() ?? '')

                const materialNumbers = Array.from(matchLookup.keys()).sortBy(n => n)

                for (const materialNo of materialNumbers) {
                    let directMatch: (ISearchMatch | null) = null
                    const partMatches: IPartOf[] = []

                    const materialMatches = matchLookup.get(materialNo)

                    if (materialMatches) {
                        for (const match of materialMatches) {
                            const node = match.node
                            const parentMaterial = node.topMostParentMaterialOf

                            if (parentMaterial == node) {
                                directMatch = match
                            }
                            else if (parentMaterial != null) {
                                partMatches.push({
                                    match,
                                    partOf: parentMaterial
                                })
                            }
                        }

                        const firstMatch = materialMatches[0].node
                        const title = `${firstMatch.materialNo} ${firstMatch.description}`
                        groups.push({ title, directMatch, partMatches } as ISearchResultGroup)
                    }
                }

                return { groups } as ISearchResult
            }

            function getSearchMatches(target: ISearchTarget, nodes: MaterialListItem[]): ISearchMatch[] {
                const result: ISearchMatch[] = []

                for (const node of nodes) {
                    const searchMatch = getSearchMatch(target, node)

                    if (searchMatch) {
                        result.push(searchMatch)
                    }

                    if (node.childItems) {
                        const descendantMatches = getSearchMatches(target, node.childItems)
                        result.push(...descendantMatches)
                    }
                }

                return result
            }

            function getSearchMatch(target: ISearchTarget, node: MaterialListItem): ISearchMatch | null {

                if (node.isSpecialNode) return null

                // Matches material number?
                if (node.materialNo && searchMatchesNumber(target, node.materialNo)) {
                    return {
                        node,
                        matchType: 'materialNo',
                    }
                }

                const targetText = target?.searchText
                const targetRegex = target?.searchRegex
                if (targetText || targetRegex) {
                    // Matches description?
                    let matchRange = searchMatchesText(target, node.description)

                    if (matchRange != null) {
                        return {
                            node,
                            matchType: 'description',
                            matchRange
                        }
                    }

                    if (node.salesText) {
                        // Matches sales text?
                        matchRange = searchMatchesText(target, node.salesText)
                        if (matchRange != null) {
                            return {
                                node,
                                matchType: 'salesText',
                                matchRange
                            }
                        }
                    }
                }

                return null
            }

            function searchMatchesNumber(target: ISearchTarget, n: number): boolean {
                return n === target?.searchNumber
            }

            function searchMatchesText(target: ISearchTarget, text: string): { startIndex: number, endIndex: number } | null {
                if (target?.searchRegex) {
                    const matches = target?.searchRegex.exec(text)

                    if (matches?.length) {
                        const firstMatch = matches[0]
                        const startIndex = text.indexOf(firstMatch)
                        const endIndex = startIndex + firstMatch.length
                        return { startIndex, endIndex }
                    }
                }
                else if (target?.searchText) {
                    const startIndex = text.toLowerCase().indexOf(target?.searchText)

                    if (startIndex >= 0) {
                        return {
                            startIndex,
                            endIndex: startIndex + target?.searchText.length
                        }
                    }
                }

                return null
            }

            function getSearchRegex(searchText: string): RegExp | null {
                if (!searchText.includes('*')) return null

                const pattern = searchText
                    .replaceAll(/[/\-\\^$+?.()|[\]{}]/g, '\\$&')
                    .replaceAll('*', '.*')

                return new RegExp(pattern, 'i') // case-insensitive matching
            }

            /********************************************************************************************************************
             WATCHERS
             ********************************************************************************************************************/
            watchEffect(() => determineSearchResults(props.searchInput))

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
